import { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { cn } from '../../../../shared';
import {
  emptyAvailability,
  WEEKDAY_LABELS,
  WEEKDAYS,
  type LinkInterviewer,
  type MeetingType,
  type Weekday,
} from '../../services/interviewLinksApi';
import type { DirectoryPerson } from '../../services/directoryApi';
import { PersonPicker } from './PersonPicker';

const TIMEZONES = [
  'Europe/Berlin',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Warsaw',
  'Europe/Lisbon',
  'Asia/Dubai',
  'Asia/Kolkata',
  'America/New_York',
];

/**
 * Interviewers plus their weekly bookable windows.
 *
 * Windows are per interviewer and in THEIR timezone — a London interviewer's
 * 09:00–17:00 is not Berlin's, and the slot engine compares in each person's own
 * zone. Windows say when someone is *willing* to interview; their calendar then
 * removes what is already booked.
 */
export function InterviewerEditor({
  meetingType,
  interviewers,
  onChange,
  embedded = false,
}: {
  meetingType: MeetingType;
  interviewers: LinkInterviewer[];
  onChange: (next: LinkInterviewer[]) => void;
  /**
   * Render only the people + availability controls, without the section frame
   * and "Interviewers" heading — for hosting inside another section that brings
   * its own heading (the feedback-call editor).
   */
  embedded?: boolean;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  function add(person: DirectoryPerson) {
    const email = person.email.trim().toLowerCase();
    if (!email || interviewers.some((i) => i.email === email)) return;
    onChange([
      ...interviewers,
      {
        email,
        // The directory gives us the display name, so the list can show people
        // rather than addresses.
        name: person.name || null,
        timezone: 'Europe/Berlin',
        availability: emptyAvailability(),
        dailyLimit: null,
      },
    ]);
    setExpanded(email);
  }

  function patch(email: string, changes: Partial<LinkInterviewer>) {
    onChange(interviewers.map((i) => (i.email === email ? { ...i, ...changes } : i)));
  }

  function setWindows(email: string, day: Weekday, windows: LinkInterviewer['availability'][Weekday]) {
    const person = interviewers.find((i) => i.email === email);
    if (!person) return;
    patch(email, { availability: { ...person.availability, [day]: windows } });
  }

  const body = (
    <>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {interviewers.map((i) => (
          <span
            key={i.email}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-800"
            title={i.email}
          >
            {i.name || i.email}
            <button
              type="button"
              aria-label={`Remove ${i.email}`}
              onClick={() => onChange(interviewers.filter((x) => x.email !== i.email))}
              className="text-gray-400 hover:text-gray-700"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
      </div>

      <div className="mt-3">
        <PersonPicker
          onSelect={add}
          excludeEmails={interviewers.map((i) => i.email)}
          placeholder="Start typing a name — e.g. Anna"
        />
      </div>

      {interviewers.length === 0 ? (
        <p className="mt-3 text-xs text-gray-500">No interviewers yet — add at least one.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {interviewers.map((person) => {
            const open = expanded === person.email;
            return (
              <div key={person.email} className="rounded-xl border border-gray-200">
                <button
                  type="button"
                  onClick={() => setExpanded(open ? null : person.email)}
                  className="flex w-full items-center justify-between px-3 py-2.5 text-left"
                >
                  <span className="text-sm font-medium text-gray-900">
                    {person.name || person.email}
                    {person.name ? (
                      <span className="ml-1.5 font-normal text-gray-500">{person.email}</span>
                    ) : null}
                  </span>
                  <span className="text-xs text-gray-500">
                    {person.timezone} · {open ? 'Hide' : 'Time view'}
                  </span>
                </button>

                {open ? (
                  <div className="border-t border-gray-200 p-3">
                    <div className="mb-3 flex flex-wrap gap-3">
                      <label className="text-xs">
                        <span className="mb-1 block font-medium text-gray-700">Timezone</span>
                        <select
                          value={person.timezone}
                          onChange={(e) => patch(person.email, { timezone: e.target.value })}
                          className="rounded-xl border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          {TIMEZONES.map((tz) => (
                            <option key={tz} value={tz}>
                              {tz}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs">
                        <span className="mb-1 block font-medium text-gray-700">
                          Interview limit per day
                        </span>
                        <select
                          value={person.dailyLimit ?? ''}
                          onChange={(e) =>
                            patch(person.email, {
                              dailyLimit: e.target.value ? Number(e.target.value) : null,
                            })
                          }
                          className="rounded-xl border border-gray-300 px-2 py-1.5 text-sm"
                        >
                          <option value="">No limit</option>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="space-y-1.5">
                      {WEEKDAYS.map((day) => (
                        <DayRow
                          key={day}
                          day={day}
                          windows={person.availability[day] || []}
                          onChange={(w) => setWindows(person.email, day, w)}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  if (embedded) return body;
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-sm font-semibold text-gray-900">
        Interviewers <span className="text-red-600">*</span>
      </h2>
      <p className="mt-1 text-xs text-gray-500">
        {meetingType === '1:1'
          ? 'The first available interviewer will be scheduled when the candidate selects a time.'
          : 'All interviewers need to be available in order for the candidate to select a time.'}
      </p>
      {body}
    </section>
  );
}

function DayRow({
  day,
  windows,
  onChange,
}: {
  day: Weekday;
  windows: { start: string; end: string }[];
  onChange: (windows: { start: string; end: string }[]) => void;
}) {
  const enabled = windows.length > 0;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex w-[132px] shrink-0 items-center gap-2 text-sm text-gray-800">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => onChange(e.target.checked ? [{ start: '09:00', end: '17:00' }] : [])}
          className="h-4 w-4 rounded border-gray-300"
        />
        {WEEKDAY_LABELS[day]}
      </label>

      {enabled ? (
        <div className="flex flex-wrap items-center gap-2">
          {windows.map((w, idx) => (
            <span key={idx} className="flex items-center gap-1.5">
              <input
                type="time"
                value={w.start}
                onChange={(e) =>
                  onChange(windows.map((x, i) => (i === idx ? { ...x, start: e.target.value } : x)))
                }
                className="rounded-xl border border-gray-300 px-2 py-1 text-sm"
              />
              <span className="text-xs text-gray-500">to</span>
              <input
                type="time"
                value={w.end}
                onChange={(e) =>
                  onChange(windows.map((x, i) => (i === idx ? { ...x, end: e.target.value } : x)))
                }
                className={cn(
                  'rounded-xl border px-2 py-1 text-sm',
                  w.end <= w.start ? 'border-red-400 bg-red-50' : 'border-gray-300'
                )}
              />
              {windows.length > 1 ? (
                <button
                  type="button"
                  aria-label="Remove window"
                  onClick={() => onChange(windows.filter((_, i) => i !== idx))}
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </span>
          ))}
          {/* A second window per day covers the common "mornings and late afternoons" case. */}
          <button
            type="button"
            aria-label={`Add another window on ${WEEKDAY_LABELS[day]}`}
            onClick={() => onChange([...windows, { start: '09:00', end: '17:00' }])}
            className="rounded border border-gray-300 px-1.5 text-gray-500 hover:border-gray-500"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <span className="text-xs text-gray-400">Unavailable</span>
      )}
    </div>
  );
}
